import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useContact, useContactMutations } from '@/hooks/contacts/useContacts';
import { DetailPageLayout } from '@/shared/layouts/DetailPageLayout';
import { Spinner } from '@/shared/components/ui/Spinner';
import { Button } from '@/shared/components/ui/Button';
import { ContactDrawerContent } from '@/components/sales/contacts/ContactDrawer';
import { ContactFormModal } from '@/components/sales/contacts/ContactFormModal';
import { ROUTES } from '@/routes/paths';
import type { CreateContactPayload, UpdateContactPayload } from '@/types/contacts';

export function ContactDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: contact, isLoading } = useContact(id || null);
  const { updateContact } = useContactMutations();
  const [editOpen, setEditOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (!contact || !id) {
    return (
      <DetailPageLayout
        title="Contact Not Found"
        breadcrumbs={[{ label: 'Contacts', to: ROUTES.contacts }, { label: 'Not Found' }]}
        onBack={() => navigate(ROUTES.contacts)}
      >
        <div className="rounded-2xl border border-outline/70 bg-surface-bright p-8 text-center">
          <p className="text-on-surface-variant">This contact could not be found or you may not have permission to view it.</p>
          <Button className="mt-4" onClick={() => navigate(ROUTES.contacts)}>
            Return to Contacts
          </Button>
        </div>
      </DetailPageLayout>
    );
  }

  const handleEditSubmit = async (payload: CreateContactPayload | UpdateContactPayload) => {
    try {
      await updateContact.mutateAsync({ id, payload: payload as UpdateContactPayload });
      toast.success('Contact updated');
      setEditOpen(false);
    } catch {
      toast.error('Failed to update contact');
    }
  };

  return (
    <DetailPageLayout
      title={contact.name}
      subtitle={[contact.position, contact.company].filter(Boolean).join(' • ') || undefined}
      breadcrumbs={[{ label: 'Contacts', to: ROUTES.contacts }, { label: contact.name }]}
      onBack={() => navigate(ROUTES.contacts)}
      backLabel="Back to Contacts"
    >
      <ContactDrawerContent
        contact={contact}
        isPage
        onClose={() => navigate(ROUTES.contacts)}
        onEdit={() => setEditOpen(true)}
      />

      <ContactFormModal
        open={editOpen}
        contact={contact}
        loading={updateContact.isPending}
        onClose={() => setEditOpen(false)}
        onSubmit={handleEditSubmit}
      />
    </DetailPageLayout>
  );
}

export default ContactDetailPage;
